// Voice control's fast path (Kareem, 4 Nov: "go to my reminders" showed Thinking and took seconds). The words are
// matched here, in the browser, as they arrive: a verb (open, go to, show, take me to, search, find, filter, back,
// scroll, new, close) and a target (a page by any of its names, or a space, track, activity, meeting, folder, file or
// person the person can see, from GET /api/voice/index). Filler words ("uh", "can you", "please") are ignored and
// names match with small slips ("steering commitee"). Only a confident, unambiguous match acts; anything else (create,
// remind, update, several steps, questions) goes to the assistant. Pure functions: no DOM, tested in apps/web/test.
import type { VoiceTarget } from '@dpw/shared';

/** A page of the app and the words people use for it. */
export interface PageDef { name: string; path: string; words: string[] }
export const PAGES: PageDef[] = [
  { name: 'Today', path: '/', words: ['today', 'home', 'my day', 'home page', 'start page', 'dashboard'] },
  { name: 'Calendar', path: '/calendar', words: ['calendar', 'my calendar', 'schedule', 'my schedule', 'agenda', 'diary'] },
  { name: 'Reminders', path: '/reminders', words: ['reminders', 'my reminders', 'reminder list', 'reminders page', 'my reminder'] },
  { name: 'Assistant', path: '/assistant', words: ['assistant', 'the assistant', 'ai', 'chat', 'ai assistant', 'conversations'] },
  { name: 'Requests', path: '/requests', words: ['requests', 'my requests', 'approvals', 'pending approvals', 'cards', 'waiting cards', 'things to approve'] },
  { name: 'Notifications', path: '/notifications', words: ['notifications', 'my notifications', 'alerts', 'notification centre', 'notification center'] },
  { name: 'Settings', path: '/settings', words: ['settings', 'preferences', 'my settings', 'account'] },
  { name: 'Usage', path: '/settings/usage', words: ['usage', 'usage page', 'costs'] },
  { name: 'Knowledge base', path: '/folders', words: ['knowledge base', 'knowledge', 'the knowledge base', 'folders', 'documents', 'document folders', 'kb'] },
  { name: 'Research Center', path: '/knowledge', words: ['research', 'research center', 'research centre', 'insights', 'research centre page'] },
  { name: 'Search', path: '/search', words: ['search', 'search page'] },
  { name: 'Meetings', path: '/meetings', words: ['meetings', 'my meetings', 'meetings page', 'recordings'] },
  { name: 'Record a meeting', path: '/record', words: ['record a meeting', 'recorder', 'record page'] },
];

export type Target = { kind: 'page'; id: string; name: string; path: string; where: null; spaceId: null } | VoiceTarget;
export type FormKind = 'activity' | 'reminder' | 'track' | 'folder' | 'insight' | 'research';
export type Intent =
  | { type: 'open'; target: Target }
  | { type: 'search'; q: string }
  | { type: 'back' }
  | { type: 'scroll'; to: 'up' | 'down' | 'top' | 'bottom' }
  | { type: 'new'; what: FormKind; activityType: 'meeting' | 'workshop' | 'deliverable' | 'task' | null }
  | { type: 'close' }
  | { type: 'filter'; params: Record<string, string>; label: string };
/** act: do it now (label for the indicator). wait: could still become a command, keep listening. assistant: not ours. */
export type Decision = { kind: 'act'; intent: Intent; label: string } | { kind: 'wait' } | { kind: 'assistant' };

// ---------- words ----------
/** Lower case, letters, digits and spaces only. */
export function norm(s: string): string {
  return s.toLowerCase().replace(/[’']/g, '').replace(/&/g, ' and ').replace(/[^\p{L}\p{N}]+/gu, ' ').replace(/\s+/g, ' ').trim();
}
const FILLER_LEAD = /^(?:(?:uh+|um+|erm+|er|ah+|hmm+|mm+|hey|ok(?:ay)?|so|right|alright|well|now|and|then|oh|yeah|yes|please|just|quickly|actually|voice control|delta)\s+)+/;
const POLITE_LEAD = /^(?:(?:can|could|would|will) you(?: please)?|please|i want to|i wanna|i would like to|id like to|i need to|lets|let us|i want you to|help me|go ahead and|try to|try and)\s+/;
const FILLER_TAIL = /(?:\s+(?:please|thanks|thank you|now|for me|right now|quickly|uh+|um+|okay|ok))+$/;
const FILLER_INNER = /\s(?:uh+|um+|erm+|er|ah+|hmm+|like)(?=\s)/g;

/** The words with filler taken out: "uh can you please go to um my reminders thanks" is "go to my reminders". */
export function clean(s: string): string {
  let t = ` ${norm(s)} `.replace(FILLER_INNER, ' ').replace(FILLER_INNER, ' ').replace(/\s+/g, ' ').trim();
  for (let i = 0; i < 4; i++) {
    const before = t;
    t = t.replace(FILLER_LEAD, '').replace(POLITE_LEAD, '').replace(FILLER_TAIL, '').replace(/\s+/g, ' ').trim();
    if (t === before) break;
  }
  return t;
}

/** Edit distance (Levenshtein), stopping early above max. */
export function distance(a: string, b: string, max = Infinity): number {
  if (a === b) return 0;
  if (Math.abs(a.length - b.length) > max) return max + 1;
  let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    let best = i;
    for (let j = 1; j <= b.length; j++) {
      cur[j] = Math.min(prev[j]! + 1, cur[j - 1]! + 1, prev[j - 1]! + (a[i - 1] === b[j - 1] ? 0 : 1));
      if (cur[j]! < best) best = cur[j]!;
    }
    if (best > max) return max + 1;
    prev = cur;
  }
  return prev[b.length]!;
}
/** 1 for the same words, less for each slip (by characters). */
const similarity = (a: string, b: string) => {
  const n = Math.max(a.length, b.length);
  if (!n) return 1;
  const d = distance(a, b, Math.ceil(n * 0.2));
  return 1 - d / n;
};

// ---------- the names ----------
interface Entry { target: Target; key: string }
const KIND_WORDS: Record<string, string[]> = {
  page: ['page'], space: ['space'], track: ['track', 'workstream'], activity: ['activity', 'task', 'deliverable'], meeting: ['meeting', 'workshop'],
  folder: ['folder'], file: ['file', 'document', 'doc'], person: ['person', 'profile'],
};
const KIND_OF_WORD: Record<string, Target['kind']> = Object.fromEntries(Object.entries(KIND_WORDS).flatMap(([k, ws]) => ws.map(w => [w, k as Target['kind']])));
/** File names are said without the extension ("data protection policy v3" for "Data Protection Policy v3.pdf"). */
const stripExt = (s: string) => s.replace(/\.(pdf|docx?|xlsx?|pptx?|csv|txt|png|jpe?g|md)$/i, '');

/** Every name, ready to match (made once for an index). */
export class NameIndex {
  readonly entries: Entry[] = [];
  constructor(targets: VoiceTarget[]) {
    for (const p of PAGES) {
      const target: Target = { kind: 'page', id: p.path, name: p.name, path: p.path, where: null, spaceId: null };
      for (const w of new Set([p.name, ...p.words])) this.entries.push({ target, key: norm(w) });
    }
    for (const t of targets) {
      const key = norm(stripExt(t.name)).replace(/^the /, '');
      if (key) this.entries.push({ target: t, key });
      // "Dr. Amir Saleh" is also "Amir Saleh"; people also by their first name (only when no one else has it).
      if (t.kind === 'person') {
        const plain = key.replace(/^(dr|mr|mrs|ms|eng) /, '');
        if (plain !== key) this.entries.push({ target: t, key: plain });
      }
    }
    const firsts = new Map<string, Target[]>();
    for (const t of targets) if (t.kind === 'person') {
      const f = norm(t.name).replace(/^(dr|mr|mrs|ms|eng) /, '').split(' ')[0]!;
      firsts.set(f, [...(firsts.get(f) ?? []), t]);
    }
    for (const [f, ts] of firsts) if (f.length >= 3) for (const t of ts) this.entries.push({ target: t, key: f });
  }

  /**
   * The target a phrase names. confident: one target scores clearly best (exact, or a small slip in a longer name).
   * prefix: the phrase is the start of a longer name (more words may be coming).
   */
  match(phrase: string, opts: { kind?: Target['kind'] | null; pagesOnly?: boolean } = {}): { target: Target; score: number; confident: boolean; prefix: boolean } | null {
    let q = phrase.replace(/^(?:the|my|our|a|an|this|that) /, '').trim();
    let kind = opts.kind ?? null;
    // "the data readiness track", "the steering committee meeting": the kind word narrows it.
    const tail = q.split(' ').pop() ?? '';
    if (!kind && KIND_OF_WORD[tail] && q.includes(' ')) { kind = KIND_OF_WORD[tail]!; q = q.slice(0, -tail.length).trim(); }
    if (!q) return null;
    const qq = q, myq = `my ${q}`;
    const scored = new Map<string, { target: Target; score: number }>();
    const longer = new Set<string>();
    for (const e of this.entries) {
      if (opts.pagesOnly && e.target.kind !== 'page') continue;
      if (kind && e.target.kind !== kind && !(kind === 'activity' && e.target.kind === 'meeting') && !(kind === 'meeting' && e.target.kind === 'activity')) continue;
      let s = 0;
      if (e.key === qq || e.key === myq || e.key === phrase) s = 1;
      // A small slip, only in a name of more than one word or a long one ("steering commitee"); never in a short
      // single word ("strategi" is not "Strategy").
      else if ((e.key.includes(' ') && e.key.length >= 8) || e.key.length >= 10) {
        for (const x of [qq, myq]) {
          if (x.length < 5 || Math.abs(e.key.length - x.length) > Math.ceil(e.key.length * 0.2)) continue;
          const sim = similarity(x, e.key);
          if (sim >= 0.84 && sim > s) s = sim;
        }
      }
      const id = `${e.target.kind}:${e.target.id}`;
      if (e.key.length > qq.length && e.key.startsWith(qq)) longer.add(id);
      if (!s) continue;
      const cur = scored.get(id);
      if (!cur || cur.score < s) scored.set(id, { target: e.target, score: s });
    }
    const ranked = [...scored.values()].sort((a, b) => b.score - a.score);
    const best = ranked[0];
    if (!best) return null;
    const second = ranked[1];
    // Two things with the same name (or two near-equal slips) are ambiguous: never act on a guess.
    const confident = !second || best.score - second.score >= 0.08;
    // Something else has a longer name that starts with these words: more may be coming.
    const prefix = [...longer].some(id => id !== `${best.target.kind}:${best.target.id}`);
    return { ...best, confident, prefix };
  }

  /** The tracks of one space (for "filter by Data Readiness" on that space's page). */
  tracksOf(spaceId: string): VoiceTarget[] {
    return this.entries.map(e => e.target).filter((t): t is VoiceTarget => t.kind === 'track' && t.spaceId === spaceId);
  }
}

// ---------- the grammar ----------
const OPEN_VERBS = /^(?:open(?: up)?|go(?: to| into|to)?|goto|show(?: me)?|take me(?: to)?|bring up|pull up|navigate to|switch to|jump to|head to|view|display|get me|load|visit|bring me to)(?: (?:the|my|our))? ?(.*)$/;
const SEARCH_VERBS = /^(?:search(?: for)?|look up|look for|search the app for)\s+(.+)$/;
const FIND_VERB = /^find(?: me)?\s+(.+)$/;
const BACK = /^(?:(?:go )?back|go back(?: a page| one page)?|previous page|take me back|last page)$/;
const SCROLL = /^(?:scroll|page|move|go)\s+(up|down|to the top|to the bottom|to top|to bottom|top|bottom)(?: a bit| a little| more)?$/;
const SCROLL_SHORT = /^(?:top of (?:the )?page|bottom of (?:the )?page|scroll)$/;
const CLOSE = /^(?:close|dismiss|cancel|hide)(?: (?:it|this|that|the))?(?: (?:sheet|panel|dialog|drawer|form|window|popup))?$|^(?:never mind|nevermind|escape)$/;
const NEW = /^(?:new|create|add|make|start|open)(?: (?:a|an|another|the))?(?: new)? (activity|task|meeting|workshop|deliverable|reminder|track|folder|insight|research task|research)(?: form)?$/;
const FILTER_TYPES: Record<string, string> = { task: 'task', tasks: 'task', meeting: 'meeting', meetings: 'meeting', workshop: 'workshop', workshops: 'workshop', deliverable: 'deliverable', deliverables: 'deliverable' };
const FILTER_TYPE = /^(?:filter(?: by| to| on| for)?|show(?: me)?|only show|just show|show only)(?: only)?( my| mine)?(?: the)?(?: open)? (tasks?|meetings?|workshops?|deliverables?)(?: only)?$/;
const FILTER_MINE = /^(?:filter(?: by| to| on| for)?|show(?: me)?|only show|show only) (?:only )?(?:mine|my (?:stuff|activities|work|items))$/;
const FILTER_DONE = /^(?:filter(?: by| to| on| for)?|show(?: me)?|include) (?:the )?(?:done|completed|finished)(?: ones| activities| items)?$/;
const FILTER_TRACK = /^filter(?: by| to| on| for)?(?: the)? (.+?)(?: track)?$/;
/**
 * A question (6.3, Kareem 5 Nov: "what happened to the steering committee" must be answered, not opened): it starts with a
 * question word, so it is never a command, however much of it is a name. The assistant answers it from its tools.
 */
const QUESTION = /^(?:what|whats|who|whos|whom|whose|when|whens|where|wheres|why|how|hows|which|is|isnt|are|arent|am|was|were|do|does|did|dont|doesnt|didnt|have|has|had|havent|hasnt|any|anything|tell me|brief me|should|shall|can i|could i|may i|remind me what)\b/;
/** After an open verb, a question: "show me what changed", "tell me when". */
const QUESTION_AFTER_VERB = /^(?:what|whats|who|whos|when|where|why|how|which|if|whether|anything)\b/;
/** Words that make a phrase more than a command ("go to reminders and add one"): the assistant takes those. */
const MORE = /\b(?:and|then|after that|also|but)\b/;

/** What these words should do (see Decision). final: the end of the turn (no more words coming). path: the page now. */
export function decide(raw: string, index: NameIndex, o: { final: boolean; path: string }): Decision {
  const t = clean(raw);
  if (!t) return o.final ? { kind: 'assistant' } : { kind: 'wait' };
  const wait: Decision = o.final ? { kind: 'assistant' } : { kind: 'wait' };
  // Questions go to the assistant (on the pause), never to the matcher. ("Could you open my calendar?" is a request:
  // its polite start is taken off first, and it opens.)
  if (QUESTION.test(t)) return wait;

  if (BACK.test(t)) return { kind: 'act', intent: { type: 'back' }, label: 'Going back' };
  const sc = SCROLL.exec(t);
  if (sc) {
    const w = sc[1]!;
    const to = w.includes('top') ? 'top' : w.includes('bottom') ? 'bottom' : (w as 'up' | 'down');
    return { kind: 'act', intent: { type: 'scroll', to }, label: to === 'up' || to === 'down' ? `Scrolling ${to}` : `Scrolling to the ${to}` };
  }
  if (SCROLL_SHORT.test(t)) {
    const to = t.startsWith('top') ? 'top' : t.startsWith('bottom') ? 'bottom' : 'down';
    return { kind: 'act', intent: { type: 'scroll', to }, label: to === 'down' ? 'Scrolling down' : `Scrolling to the ${to}` };
  }
  if (CLOSE.test(t)) return { kind: 'act', intent: { type: 'close' }, label: 'Closing' };
  const nw = NEW.exec(t);
  if (nw) {
    const w = nw[1]!;
    const what: FormKind = w === 'reminder' ? 'reminder' : w === 'track' ? 'track' : w === 'folder' ? 'folder' : w === 'insight' ? 'insight' : w.startsWith('research') ? 'research' : 'activity';
    const activityType = (['meeting', 'workshop', 'deliverable', 'task'] as const).find(x => x === w) ?? null;
    const label = what === 'activity' ? `New ${activityType ?? 'activity'}` : what === 'research' ? 'New research task' : what === 'insight' ? 'Add an insight' : what === 'reminder' ? 'Add reminder' : `New ${what}`;
    return { kind: 'act', intent: { type: 'new', what, activityType }, label };
  }
  if (/^(?:new|create|add|make)\b/.test(t)) return wait;

  // Filters (a space's list), only on a space's page.
  const space = /^\/spaces\/([0-9a-f-]{36})/.exec(o.path)?.[1] ?? null;
  if (space) {
    const ft = FILTER_TYPE.exec(t);
    if (ft) {
      const type = FILTER_TYPES[ft[2]!]!;
      const params: Record<string, string> = { type, ...(ft[1] ? { mine: '1' } : {}) };
      return { kind: 'act', intent: { type: 'filter', params, label: `${ft[1] ? 'my ' : ''}${type}s` }, label: `Showing ${ft[1] ? 'my ' : ''}${type}s` };
    }
    if (FILTER_MINE.test(t)) return { kind: 'act', intent: { type: 'filter', params: { mine: '1' }, label: 'mine' }, label: 'Showing mine' };
    if (FILTER_DONE.test(t)) return { kind: 'act', intent: { type: 'filter', params: { done: '1' }, label: 'done' }, label: 'Showing done ones too' };
    const ftr = FILTER_TRACK.exec(t);
    if (ftr) {
      const name = norm(ftr[1]!).replace(/^the /, '');
      const hits = index.tracksOf(space).filter(x => norm(x.name) === name || (name.length >= 5 && similarity(name, norm(x.name)) >= 0.84));
      if (hits.length === 1) return { kind: 'act', intent: { type: 'filter', params: { track: hits[0]!.id }, label: hits[0]!.name }, label: `Showing ${hits[0]!.name}` };
      return wait;
    }
  }
  if (/^filter\b/.test(t)) return wait;

  const se = SEARCH_VERBS.exec(t);
  if (se) {
    // Search acts on a pause only: the words may still be coming.
    const q = se[1]!.trim();
    return o.final && q && !MORE.test(q) ? { kind: 'act', intent: { type: 'search', q }, label: `Searching for ${q}` } : wait;
  }
  if (/^(?:search|look up|look for)$/.test(t)) return o.final ? { kind: 'act', intent: { type: 'open', target: pageTarget('/search') }, label: 'Opening Search' } : wait;

  const fd = FIND_VERB.exec(t);
  if (fd) {
    const q = fd[1]!.trim();
    const m = index.match(q);
    if (m && m.score === 1 && m.confident && (o.final || !m.prefix)) return { kind: 'act', intent: { type: 'open', target: m.target }, label: `Opening ${m.target.name}` };
    return o.final && q && !MORE.test(q) ? { kind: 'act', intent: { type: 'search', q }, label: `Searching for ${q}` } : wait;
  }

  const op = OPEN_VERBS.exec(t);
  const phrase = op ? op[1]!.trim() : t;
  if (op && !phrase) return wait;
  if (op && QUESTION_AFTER_VERB.test(phrase)) return wait;
  // Without a verb, a page's name opens it only at the pause: mid-sentence it may be the start of a question ("reminders
  // for tomorrow?", "today's meetings").
  if (!op && !o.final) return wait;
  // Without a verb, only a page's own name ("reminders", "my calendar") opens it.
  const m = index.match(phrase, { pagesOnly: !op });
  if (!m || !m.confident) return wait;
  // Mid-sentence, a name that is the start of a longer one ("data" for "data readiness") waits for the rest.
  if (!o.final && m.prefix) return { kind: 'wait' };
  return { kind: 'act', intent: { type: 'open', target: m.target }, label: `Opening ${m.target.name}` };
}

const pageTarget = (path: string): Target => {
  const p = PAGES.find(x => x.path === path)!;
  return { kind: 'page', id: p.path, name: p.name, path: p.path, where: null, spaceId: null };
};

/** Two decisions do the same thing (the words grew but still mean it). */
export const sameAct = (a: Decision, b: Decision) => a.kind === 'act' && b.kind === 'act' && a.label === b.label;
